import React from 'react'

import { FormProvider } from 'react-hook-form'
import { ComGate } from 'shopify-payments-types'

import {
  ActionMenu,
  Banner,
  Button,
  Card,
  Collapsible,
  Form,
  Heading,
  Stack,
  TextContainer,
} from '@shopify/polaris'

import { useAccount } from '../hooks/useAccount'
import { useAccountSettingsForm } from '../hooks/useAccountSettingsForm'
import { useBackToShopAction } from '../hooks/useBackToShopAction'
import { getTranslation } from '../util/getTranslation'
import { SettingsContactCard } from './SettingsContactCard/SettingsContactCard'

type TProps = {
  onSuccess(): void
}

export function ComGateRegistration({ onSuccess }: TProps) {
  const {
    segmentConfig: { registrationSentAt },
  } = useAccount<ComGate.TSegmentAccount>()

  const { saveStatus, form, onSubmit } = useAccountSettingsForm(
    (api, formValues) => api.registerComGate(formValues),
  )

  React.useEffect(() => {
    if (registrationSentAt) {
      onSuccess()
    }
  }, [registrationSentAt, onSuccess])

  return (
    <Card
      sectioned
      title={getTranslation(
        'Registrace Comgate účtu',
        'Registration of Comgate account',
      )}
    >
      <Stack vertical>
        <Collapsible id="new" open={saveStatus === 'submit'}>
          <TextContainer>
            <p>
              {getTranslation(
                'Na základě tohoto formuláře pošleme do Comgate emailovou zprávu obsahující údaje pro nastavení komunikace s tímto účtem. V následujících dnech Vás bude Comgate kontaktovat s dalším postupem.',
                "Based on this form we'll sent an email to Comgate with the details for setting up the communication with this account. In the next few days Comgate will be in touch with you with next steps.",
              )}
            </p>
            <p>
              {getTranslation(
                'Máte již Comgate účet zřízený? Odešlete formulář i v takovém případě a Comgate se postará o nastavení Vašeho účtu. Ujistěte se, že zadáváte stejnou emailovou adresu jako máte ve vytvořeném Comgate účtu.',
                'Do you already have a Comgate account? Submit the form even then and Comgate will take care of setting up your account. Make sure you enter the same email address as you have in the created Comgate account.',
              )}
            </p>
          </TextContainer>
          <FormProvider {...form}>
            <Form onSubmit={onSubmit}>
              <SettingsContactCard />
              <Card.Section>
                <Stack distribution="trailing">
                  <Button primary submit disabled={form.formState.isSubmitting}>
                    {getTranslation('Odeslat registraci', 'Sent registration')}
                  </Button>
                </Stack>
              </Card.Section>
            </Form>
          </FormProvider>
        </Collapsible>
        <Collapsible id="done" open={saveStatus === 'success'}>
          <Stack vertical>
            <Banner status="success">
              <Heading>
                {getTranslation(
                  'Registrace byla odeslána.',
                  'Registration was sent.',
                )}
              </Heading>
            </Banner>
            <TextContainer>
              <p>
                {getTranslation(
                  'Jakmile bude Vaše komunikace s Comgate vyřešena, použijte stejný odkaz k instalaci této aplikace a vraťte se sem pro dokončení procesu.',
                  'Once your communication with Comgate is resolved, use the same link to install this application and return here to complete the process.',
                )}
              </p>
            </TextContainer>
            <ActionMenu actions={[useBackToShopAction()]} />
          </Stack>
        </Collapsible>
      </Stack>
    </Card>
  )
}
